import React, { useState, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Skeleton,
  Alert,
  InputBase,
  Chip,
  Snackbar,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { palette } from '../../theme/colors';

interface B2BProductResponse {
  product: {
    id: string;
    name: string;
    description: string;
    category: string;
    price: number;
    images: Array<{ thumbnail: string; url: string }>;
    isB2bVisible: boolean;
    b2bMinimumQuantity?: number;
  };
  finalPrice: number;
  originalPrice: number;
  discountPercentage: number;
}

interface B2BProduct {
  id: string;
  name: string;
  description: string;
  category: string;
  basePrice: number;
  finalPrice: number;
  discountAmount: number;
  images: Array<{ thumbnail: string; url: string }>;
  isB2bVisible: boolean;
  b2bMinimumQuantity?: number;
}

interface PortalContext {
  portalInfo: {
    organization: { id: string; name: string };
  };
  isAuthenticated: boolean;
}

const getAuthToken = (): string | null => {
  // Storefront stores access_token directly (not as JSON object)
  return localStorage.getItem('access_token');
};

const fetchB2BProducts = async (orgSlug: string, token: string): Promise<B2BProduct[]> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/products`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to fetch products');
  }
  const data = await response.json();

  // Handle the API response format: { products: [...], total, page, limit }
  const products = data.products || data.data || (Array.isArray(data) ? data : []);

  // Transform the response to match our interface
  return products.map((item: B2BProductResponse | B2BProduct) => {
    // Handle nested product structure from API
    if ('product' in item && item.product) {
      return {
        id: item.product.id,
        name: item.product.name,
        description: item.product.description,
        category: item.product.category,
        basePrice: item.originalPrice,
        finalPrice: item.finalPrice,
        discountAmount: item.originalPrice - item.finalPrice,
        images: item.product.images || [],
        isB2bVisible: item.product.isB2bVisible,
        b2bMinimumQuantity: item.product.b2bMinimumQuantity,
      };
    }
    // Already in correct format
    return item as B2BProduct;
  });
};

export const B2BProductsPage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useOutletContext<PortalContext>();
  const token = getAuthToken();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/b2b/${orgSlug}/login`);
    }
  }, [isAuthenticated, navigate, orgSlug]);

  const { data: products, isLoading, error } = useQuery({
    queryKey: ['b2b-products', orgSlug],
    queryFn: () => fetchB2BProducts(orgSlug!, token!),
    enabled: !!orgSlug && !!token,
  });

  // Get categories
  const categories = useMemo(() => {
    if (!products) return [];
    const cats = new Set(products.map((p) => p.category).filter(Boolean));
    return Array.from(cats);
  }, [products]);

  // Filter products
  const filteredProducts = useMemo(() => {
    return products?.filter((product) => {
      const matchesSearch =
        searchTerm === '' ||
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === '' || product.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  const handleAddToCart = (product: B2BProduct) => {
    // Get existing cart from localStorage
    const cartStr = localStorage.getItem('b2b_cart');
    const cart = cartStr ? JSON.parse(cartStr) : [];

    const minQty = product.b2bMinimumQuantity || 1;
    const existingIndex = cart.findIndex((item: any) => item.productId === product.id);
    if (existingIndex >= 0) {
      cart[existingIndex].quantity += 1;
    } else {
      cart.push({
        productId: product.id,
        name: product.name,
        price: product.finalPrice,
        quantity: minQty, // Start with minimum quantity
        image: product.images?.[0]?.thumbnail,
        minQuantity: minQty,
      });
    }

    localStorage.setItem('b2b_cart', JSON.stringify(cart));
    setSnackbarMessage(`${product.name} added to cart`);
    setSnackbarOpen(true);
  };

  const getImageUrl = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${path}`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          B2B Product Catalog
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse our wholesale products with your exclusive pricing
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 4,
          alignItems: 'center',
        }}
      >
        {/* Category Pills */}
        <Chip
          label="All Products"
          onClick={() => setSelectedCategory('')}
          variant={selectedCategory === '' ? 'filled' : 'outlined'}
          sx={{
            bgcolor: selectedCategory === '' ? palette.primary : 'transparent',
            color: selectedCategory === '' ? '#fff' : palette.textPrimary,
            borderColor: palette.border,
          }}
        />
        {categories.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            onClick={() => setSelectedCategory(cat)}
            variant={selectedCategory === cat ? 'filled' : 'outlined'}
            sx={{
              bgcolor: selectedCategory === cat ? palette.primary : 'transparent',
              color: selectedCategory === cat ? '#fff' : palette.textPrimary,
              borderColor: palette.border,
            }}
          />
        ))}

        {/* Search */}
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            bgcolor: palette.sky,
            borderRadius: 50,
            px: 2,
            py: 0.5,
            minWidth: 200,
          }}
        >
          <SearchOutlinedIcon sx={{ color: palette.textMuted, fontSize: 20, mr: 1 }} />
          <InputBase
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, fontSize: '0.875rem' }}
          />
        </Box>
      </Box>

      {/* Loading */}
      {isLoading && (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load products. Please try again.
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && filteredProducts?.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            bgcolor: palette.surface,
            borderRadius: 3,
            border: `1px solid ${palette.border}`,
          }}
        >
          <InventoryIcon sx={{ fontSize: 64, color: palette.textMuted, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Box>
      )}

      {/* Products Grid */}
      <Grid container spacing={3}>
        {filteredProducts?.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              {/* Badges */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  alignItems: 'flex-end',
                }}
              >
                {product.discountAmount > 0 && (
                  <Box
                    sx={{
                      bgcolor: palette.success,
                      color: '#fff',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 50,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                    }}
                  >
                    B2B Price
                  </Box>
                )}
                {product.b2bMinimumQuantity && product.b2bMinimumQuantity > 1 && (
                  <Box
                    sx={{
                      bgcolor: palette.primary,
                      color: '#fff',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 50,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                    }}
                  >
                    Min: {product.b2bMinimumQuantity}
                  </Box>
                )}
              </Box>

              {/* Product Image */}
              <Box
                component={RouterLink}
                to={`/b2b/${orgSlug}/products/${product.id}`}
                sx={{
                  height: 200,
                  bgcolor: palette.sky,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '16px 16px 0 0',
                  overflow: 'hidden',
                }}
              >
                {product.images && product.images.length > 0 ? (
                  <Box
                    component="img"
                    src={getImageUrl(product.images[0].thumbnail)}
                    alt={product.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      p: 3,
                      transition: 'transform 0.3s ease',
                      '&:hover': { transform: 'scale(1.08)' },
                    }}
                  />
                ) : (
                  <InventoryIcon sx={{ fontSize: 64, color: palette.textMuted }} />
                )}
              </Box>

              {/* Product Info */}
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                  component={RouterLink}
                  to={`/b2b/${orgSlug}/products/${product.id}`}
                  sx={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {product.name}
                </Typography>

                {product.category && (
                  <Chip
                    label={product.category}
                    size="small"
                    sx={{
                      alignSelf: 'flex-start',
                      mb: 1,
                      bgcolor: palette.sky,
                      fontSize: '0.7rem',
                    }}
                  />
                )}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    flex: 1,
                  }}
                >
                  {product.description || 'No description available'}
                </Typography>

                {/* Price */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" fontWeight={700} color={palette.primary}>
                    {product.finalPrice.toFixed(2)} DZD
                  </Typography>
                  {product.discountAmount > 0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        textDecoration: 'line-through',
                        color: palette.textMuted,
                      }}
                    >
                      {product.basePrice.toFixed(2)} DZD
                    </Typography>
                  )}
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddShoppingCartIcon />}
                    onClick={() => handleAddToCart(product)}
                    sx={{ flex: 1, borderColor: palette.border }}
                  >
                    Add to Cart
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<RequestQuoteIcon />}
                    component={RouterLink}
                    to={`/b2b/${orgSlug}/products/${product.id}`}
                    sx={{ flex: 1 }}
                  >
                    Quote
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add to Cart Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
